import React, { useState, useEffect, useRef } from 'react';
import { useLang } from '../context/LanguageContext';
import FormShell, { Field, inputCls } from './FormShell';
import { submitToSheet, generateSubmissionId, getVisitorId, sanitizePhone } from '../lib/api';

const CallbackModal = ({ open, onClose, pageSource = 'services', serviceName = '' }) => {
  const { t, lang } = useLang();
  const initial = { full_name: '', phone: '', country: '', contact_method: '', preferred_date: '', preferred_time: '', timezone: '', additional_notes: '', consent: false };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refNo, setRefNo] = useState('');
  const submitLock = useRef(false);

  useEffect(() => { if (!open) { setSuccess(false); setRefNo(''); setForm(initial); submitLock.current = false; setLocked(false); } /* eslint-disable-next-line */ }, [open]);

  if (!open) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setLocked(true);
    const submission_id = generateSubmissionId('SIB-ADV');
    let notes = form.additional_notes || '';
    if (serviceName) {
      const suffix = `Service Enquiry: ${serviceName}`;
      notes = notes ? `${notes}\n${suffix}` : suffix;
    }
    const payload = {
      form_type: 'advisor_callback',
      submission_id,
      visitor_id: getVisitorId(),
      full_name: form.full_name,
      phone: form.phone,
      country: form.country,
      contact_method: form.contact_method,
      preferred_date: form.preferred_date,
      preferred_time: form.preferred_time,
      timezone: form.timezone,
      additional_notes: notes,
      language: lang,
      page_source: pageSource,
      status: 'callback_requested'
    };
    await submitToSheet(payload);
    setRefNo(submission_id);
    setSubmitting(false);
    setSuccess(true);
    setTimeout(() => { submitLock.current = false; setLocked(false); }, 4000);
  };

  const handleReset = () => { setSuccess(false); setRefNo(''); setForm(initial); submitLock.current = false; setLocked(false); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}>
        <FormShell
          title={t.forms.talkAdvisorTitle}
          desc={t.forms.talkAdvisorDesc}
          onSubmit={handleSubmit}
          submitting={submitting}
          locked={locked}
          success={success}
          onReset={handleReset}
          close={onClose}
          refNo={refNo}
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t.forms.fullName} required>
              <input required className={inputCls} value={form.full_name} onChange={e => set('full_name', e.target.value)} />
            </Field>
            <Field label={t.forms.phone} required>
              <input
                required
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                pattern="[0-9+\s\-()]{6,20}"
                title={t.forms.phoneHelp}
                className={inputCls}
                dir="ltr"
                value={form.phone}
                onChange={e => set('phone', sanitizePhone(e.target.value))}
                onKeyDown={(e) => { const allowed = /[0-9+\s\-()]/; if (e.key.length === 1 && !allowed.test(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault(); }}
              />
            </Field>
            <Field label={t.forms.country} required>
              <select required className={inputCls} value={form.country} onChange={e => set('country', e.target.value)}>
                <option value="">{t.forms.selectCountry}</option>
                {t.forms.countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t.forms.preferredContact} required>
              <select required className={inputCls} value={form.contact_method} onChange={e => set('contact_method', e.target.value)}>
                <option value="">{t.forms.selectCountry}</option>
                <option value="Phone Call">{t.forms.phoneCall}</option>
                <option value="WhatsApp Call">{t.forms.whatsappCall}</option>
                <option value="WhatsApp Message">{t.forms.whatsappMsg}</option>
              </select>
            </Field>
            <Field label={t.forms.preferredDate} required>
              <input type="date" required className={inputCls} dir="ltr" value={form.preferred_date} onChange={e => set('preferred_date', e.target.value)} />
            </Field>
            <Field label={t.forms.preferredTime} required>
              <input type="time" required className={inputCls} dir="ltr" value={form.preferred_time} onChange={e => set('preferred_time', e.target.value)} />
            </Field>
          </div>
          <Field label={t.forms.timeZone} required>
            <select required className={inputCls} value={form.timezone} onChange={e => set('timezone', e.target.value)}>
              <option value="">{t.forms.selectCountry}</option>
              {t.forms.timezones.map(tz => <option key={tz}>{tz}</option>)}
            </select>
          </Field>
          <Field label={t.forms.additionalNotes}>
            <textarea className={inputCls} rows={3} value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)} />
          </Field>
          {serviceName && (
            <div className="text-xs text-[#33473d]/70 bg-[#FBF9F2] border border-[#E8E1CE] rounded-md px-3 py-2">
              Service Enquiry: <span className="font-semibold text-[#0B5D3B]">{serviceName}</span>
            </div>
          )}
          <label className="flex items-start gap-2 text-sm text-[#33473d]">
            <input type="checkbox" required checked={form.consent} onChange={e => set('consent', e.target.checked)} className="mt-1" />
            <span>{t.forms.consent}</span>
          </label>
        </FormShell>
      </div>
    </div>
  );
};

export default CallbackModal;
